import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Clock3 } from "lucide-react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import SectionHeading from "./SectionHeading";
import ApiURl from "../controllers/Api";

gsap.registerPlugin(ScrollTrigger);

/* ============================================================
   COLORS
   ============================================================ */

const SECTION_BG = "var(--user-bg, #E8D9D5)";

/*
 * IMPORTANT:
 * Same dynamic maroon variable used by WhyKanthi.
 * So if --user-maroon changes, this heading changes automatically.
 */
const MAROON = "var(--user-maroon, #2B0000)";

const CARD_BG = "#FBF8F4";
const CARD_TEXT = "#2B1718";
const TEXT_SOFT = "var(--user-text-soft, #786B68)";
const GOLD = "var(--user-gold, #D6B33F)";

/* ============================================================
   FALLBACK BLOGS
   ============================================================ */

const fallbackBlogs = [
  {
    id: "fallback-1",
    date: "17 Jul, 2025",
    read_time: "5 min read",
    title: "How to Style Sarees for Modern Occasions",
    short_description:
      "Sarees are timeless. Here's how you can style them for today's modern events.",
    image_url:
      "https://images.unsplash.com/photo-1611591437281-460bfbe1220a?q=80&w=1200&auto=format&fit=crop",
  },
  {
    id: "fallback-2",
    date: "15 Jul, 2025",
    read_time: "4 min read",
    title: "5 Minimal Accessories That Elevate Any Look",
    short_description:
      "Less is more! These minimal pieces can instantly elevate your ethnic look.",
    image_url:
      "https://images.unsplash.com/photo-1602173574767-37ac01994b2a?q=80&w=1200&auto=format&fit=crop",
  },
  {
    id: "fallback-3",
    date: "12 Jul, 2025",
    read_time: "6 min read",
    title: "The Art of Handwoven: Our Craft, Our Pride",
    short_description:
      "Behind every weave lies a story of heritage, passion and perfection.",
    image_url:
      "https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?q=80&w=1200&auto=format&fit=crop",
  },
];

export default function BlogSection() {
  const sectionRef = useRef(null);
  const cardRefs = useRef([]);

  const [blogs, setBlogs] = useState(fallbackBlogs);

  /* ============================================================
     GET BLOGS
     ============================================================ */

  useEffect(() => {
    let mounted = true;

    fetch(`${ApiURl}getBlogs.php?active_only=1`)
      .then((response) => response.json())
      .then((response) => {
        if (
          !mounted ||
          !response?.success ||
          !Array.isArray(response.data)
        ) {
          return;
        }

        if (response.data.length > 0) {
          /*
           * Sort newest first and ONLY keep latest 3.
           *
           * If created_at exists, newest created_at wins.
           * Otherwise higher ID is treated as newer.
           */

          const latestThree = [...response.data]
            .sort((a, b) => {
              if (a.created_at && b.created_at) {
                return (
                  new Date(b.created_at).getTime() -
                  new Date(a.created_at).getTime()
                );
              }

              return Number(b.id || 0) - Number(a.id || 0);
            })
            .slice(0, 3);

          setBlogs(latestThree);
        }
      })
      .catch((error) => {
        console.error("Unable to load blogs:", error);
      });

    return () => {
      mounted = false;
    };
  }, []);

  /* ============================================================
     CARD ANIMATION
     ============================================================ */

  useEffect(() => {
    const section = sectionRef.current;
    const cards = cardRefs.current.filter(Boolean);

    if (!section || cards.length === 0) {
      return undefined;
    }

    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (prefersReducedMotion) {
      gsap.set(cards, {
        x: 0,
        y: 0,
        scale: 1,
        autoAlpha: 1,
        clearProps: "transform",
      });

      return undefined;
    }

    const context = gsap.context(() => {
      const timeline = gsap.timeline({
        paused: true,
        defaults: {
          ease: "power3.out",
        },
      });

      cards.forEach((card, index) => {
        let horizontalOffset = 0;

        if (index === 0) {
          horizontalOffset = -60;
        } else if (index === cards.length - 1) {
          horizontalOffset = 60;
        }

        timeline.fromTo(
          card,
          {
            x: horizontalOffset,
            y: 45,
            autoAlpha: 0,
            scale: 0.97,
          },
          {
            x: 0,
            y: 0,
            autoAlpha: 1,
            scale: 1,
            duration: 0.72,
            ease: "power3.out",
            overwrite: true,
          },
          index * 0.1
        );
      });

      /* TOP TO BOTTOM */

      const topToBottomTrigger = ScrollTrigger.create({
        trigger: section,
        start: "top 72%",
        invalidateOnRefresh: true,

        onEnter: () => {
          timeline.restart();
        },
      });

      /* BOTTOM TO TOP */

      const bottomToTopTrigger = ScrollTrigger.create({
        trigger: section,
        start: "bottom 92%",
        invalidateOnRefresh: true,

        onEnterBack: () => {
          timeline.restart();
        },
      });

      /* CARD HOVER */

      const hoverCleanupFunctions = [];

      cards.forEach((card) => {
        const handleMouseEnter = () => {
          if (window.innerWidth < 768) return;

          gsap.to(card, {
            y: -7,
            duration: 0.35,
            ease: "power3.out",
            overwrite: "auto",
          });
        };

        const handleMouseLeave = () => {
          if (window.innerWidth < 768) return;

          gsap.to(card, {
            y: 0,
            duration: 0.4,
            ease: "power3.out",
            overwrite: "auto",
          });
        };

        card.addEventListener("mouseenter", handleMouseEnter);
        card.addEventListener("mouseleave", handleMouseLeave);

        hoverCleanupFunctions.push(() => {
          card.removeEventListener(
            "mouseenter",
            handleMouseEnter
          );

          card.removeEventListener(
            "mouseleave",
            handleMouseLeave
          );
        });
      });

      const refreshTimer = window.setTimeout(() => {
        ScrollTrigger.refresh();
      }, 300);

      const handleWindowLoad = () => {
        ScrollTrigger.refresh();
      };

      window.addEventListener("load", handleWindowLoad);

      return () => {
        window.clearTimeout(refreshTimer);

        window.removeEventListener(
          "load",
          handleWindowLoad
        );

        hoverCleanupFunctions.forEach((cleanup) =>
          cleanup()
        );

        topToBottomTrigger.kill();
        bottomToTopTrigger.kill();
        timeline.kill();
      };
    }, section);

    return () => {
      context.revert();
    };
  }, [blogs.length]);

  /* ============================================================
     FORMAT DATE
     ============================================================ */

  const formatDate = (blog) => {
    if (blog.date) return blog.date;

    if (!blog.created_at) return "";

    return new Date(blog.created_at).toLocaleDateString(
      "en-IN",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }
    );
  };

  /* ============================================================
     RENDER
     ============================================================ */

  return (
    <section
      ref={sectionRef}
      className="
        home-blog-section
        relative
        overflow-hidden

        px-3
        py-14

        sm:px-8

        md:px-8

        lg:px-14
        lg:py-20
      "
      style={{
        backgroundColor: SECTION_BG,
      }}
    >
      <div className="relative mx-auto max-w-[1500px]">

        {/* ======================================================
            HEADING
            ====================================================== */}

        <SectionHeading
          title="LATEST BLOGS"
          subtitle="Stories, tips and craftsmanship from SL Gold"

          /*
           * SAME dynamic maroon as WhyKanthi background
           */
          titleColor={MAROON}
          lineColor={MAROON}

          subtitleColor="rgba(43,0,0,0.68)"
        />

        {/* ======================================================
            BLOG GRID
            ONLY LATEST 3
            ====================================================== */}

        <div
          className="
            mx-auto
            mt-9
            grid
            max-w-[1320px]
            gap-4

            md:grid-cols-2
            md:gap-6

            lg:mt-10
            lg:grid-cols-3
            lg:gap-7
          "
        >
          {blogs.slice(0, 3).map((blog, index) => (
            <article
              key={blog.id || blog.title}
              ref={(element) => {
                cardRefs.current[index] = element;
              }}
              className="
                group
                relative
                flex
                min-h-[182px]
                overflow-hidden

                rounded-[18px]

                border
                border-black/[0.06]

                bg-[#FBF8F4]

                shadow-[0_10px_30px_rgba(48,22,20,0.09)]

                will-change-transform

                md:min-h-[455px]
                md:flex-col
                md:rounded-[22px]
                md:shadow-[0_12px_35px_rgba(48,22,20,0.09)]
              "
              style={{
                backgroundColor: CARD_BG,
              }}
            >
              {/* =================================================
                  IMAGE
                  Mobile = left
                  Desktop = top
                  ================================================= */}

              <div
                className="
                  relative
                  min-h-[182px]
                  w-[41%]
                  shrink-0
                  overflow-hidden

                  md:h-[220px]
                  md:min-h-0
                  md:w-full
                "
              >
                <img
                  src={blog.image_url}
                  alt={blog.title}
                  loading="eager"
                  decoding="async"
                  className="
                    h-full
                    w-full
                    object-cover

                    transition-transform
                    duration-700
                    ease-out

                    group-hover:scale-[1.04]
                  "
                />

                {/* IMAGE GRADIENT */}

                <div
                  className="
                    pointer-events-none
                    absolute
                    inset-0

                    bg-gradient-to-t
                    from-black/15
                    via-transparent
                    to-black/[0.03]
                  "
                />

                {/* MOBILE INNER BORDER */}

                <div
                  className="
                    pointer-events-none
                    absolute
                    inset-[5px]

                    rounded-[13px]

                    border
                    border-white/25

                    md:hidden
                  "
                />
              </div>

              {/* =================================================
                  CONTENT
                  ================================================= */}

              <div
                className="
                  relative
                  flex
                  min-w-0
                  flex-1
                  flex-col

                  px-3
                  py-3

                  sm:px-4
                  sm:py-4

                  md:px-6
                  md:pb-6
                  md:pt-5
                "
              >
                {/* DATE + READ TIME */}

                <div
                  className="
                    home-blog-meta

                    mt-2

                    flex
                    flex-wrap
                    items-center

                    gap-x-2
                    gap-y-1

                    text-[8px]
                    font-medium

                    sm:text-[9px]

                    md:mt-0
                    md:border-b
                    md:pb-4
                    md:text-[16px]

                    lg:text-[17px]
                  "
                  style={{
                    color: TEXT_SOFT,
                    borderColor: "rgba(43,23,24,0.09)",
                  }}
                >
                  <span
                    className="
                      inline-flex
                      items-center
                      gap-1.5
                    "
                  >
                    <CalendarDays
                      size={10}
                      strokeWidth={1.8}
                      aria-hidden="true"
                      className="
                        md:h-[19px]
                        md:w-[19px]
                      "
                    />

                    <span className="whitespace-nowrap">
                      {formatDate(blog)}
                    </span>
                  </span>

                  {/* MOBILE DOT */}

                  <span
                    className="
                      h-[3px]
                      w-[3px]
                      rounded-full

                      md:hidden
                    "
                    style={{
                      backgroundColor:
                        "rgba(120,107,104,0.45)",
                    }}
                  />

                  <span
                    className="
                      inline-flex
                      items-center
                      gap-1.5
                    "
                  >
                    <Clock3
                      size={10}
                      strokeWidth={1.8}
                      aria-hidden="true"
                      className="
                        md:h-[19px]
                        md:w-[19px]
                      "
                    />

                    <span className="whitespace-nowrap">
                      {blog.read_time}
                    </span>
                  </span>
                </div>

                {/* TITLE */}

                <h3
                  className="
                    home-blog-title

                    mt-2

                    line-clamp-2

                    font-serif

                    text-[14px]
                    font-semibold
                    leading-[1.12]
                    tracking-[-0.02em]

                    sm:text-[16px]

                    md:mt-5
                    md:text-[32px]
                    md:leading-[1.16]

                    lg:text-[36px]
                  "
                  style={{
                    color: CARD_TEXT,
                  }}
                >
                  {blog.title}
                </h3>

                {/* DESCRIPTION */}

                <p
                  className="
                    home-blog-description

                    mt-1.5

                    line-clamp-2

                    text-[9px]
                    leading-[1.45]

                    sm:text-[10px]

                    md:mt-3
                    md:line-clamp-3
                    md:text-[19px]
                    md:leading-[1.65]

                    lg:text-[21px]
                  "
                  style={{
                    color: TEXT_SOFT,
                  }}
                >
                  {blog.short_description}
                </p>

                {/* READ MORE */}

                <div className="mt-auto pt-2 md:pt-5">
                  <Link
                    to={
                      String(blog.id).startsWith(
                        "fallback"
                      )
                        ? "/about-us"
                        : `/blog/${blog.id}`
                    }
                    className="
                      home-blog-read-more

                      inline-flex
                      items-center

                      gap-1.5

                      text-[10px]
                      font-semibold

                      transition-[gap,color]
                      duration-300

                      hover:gap-2.5

                      md:gap-2
                      md:text-[18px]
                      md:hover:gap-3

                      lg:text-[20px]
                    "
                    style={{
                      color: "#B48728",
                    }}
                  >
                    Read More

                    <ArrowRight
                      size={12}
                      strokeWidth={2}
                      className="
                        md:h-[21px]
                        md:w-[21px]
                      "
                    />
                  </Link>
                </div>
              </div>

              {/* =================================================
                  BOTTOM GOLD HOVER LINE
                  ================================================= */}

              <div
                className="
                  pointer-events-none
                  absolute
                  inset-x-7
                  bottom-0

                  hidden
                  h-[2px]

                  origin-center
                  scale-x-0

                  transition-transform
                  duration-500

                  group-hover:scale-x-100

                  md:block
                "
                style={{
                  background:
                    "linear-gradient(90deg, transparent, #D6B33F, transparent)",
                }}
              />
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}